// Restaurants — restaurantes tradicionales por ciudad con precio, rating y filtros.
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, Navigation, Star, Phone, Clock, Edit3, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { TRIP as STATIC_TRIP } from "../data/trip";
import { useGeolocation, distanceMeters, formatDistance, walkingMinutes } from "../hooks/useGeolocation";
import FavoriteButton from "../components/FavoriteButton";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";
import EditSheet, { Field, inputCls } from "../components/EditSheet";

function uid() { return `r-${Math.random().toString(36).slice(2, 9)}`; }
function emptyRestaurant(city) {
  return {
    id: uid(), name: "", cuisine: "", price: 2, rating: 4.0,
    area: "", address: "", lat: 0, lng: 0, hours: "",
    signature: "", noPork: false, jewish: false, reserve: "", tip: "",
    _city: city,
  };
}

const DEFAULT_CITIES = ["Budapest", "Praga", "Tel Aviv", "Madrid"];

const PRICE_LABELS = ["$", "$$", "$$$", "$$$$"];
const PRICE_TEXTS = {
  1: "< €15",
  2: "€15–35",
  3: "€35–65",
  4: "> €65",
};

function priceMatchesFilter(price, filter) {
  if (filter === "all") return true;
  return price === filter;
}

export default function Restaurants({ hideBackLink = false }) {
  const base = useTripBase();
  const [searchParams] = useSearchParams();
  const { trip, tripId, saveTripData } = useTrip();
  const T = trip && trip.restaurants ? trip : {};
  // Build city list dynamically from the trip's restaurants (fallback to defaults).
  const CITIES = useMemo(() => {
    const keys = Object.keys(T.restaurants || {});
    return keys.length ? keys : DEFAULT_CITIES;
  }, [T]);
  const initialCity = searchParams.get("city") || CITIES[0];
  const [city, setCity] = useState(
    CITIES.includes(initialCity) ? initialCity : CITIES[0]
  );
  const [priceFilter, setPriceFilter] = useState("all"); // all | 1..4
  const [noPorkOnly, setNoPorkOnly] = useState(false);
  const [sortBy, setSortBy] = useState("rating"); // rating | distance | price
  const { position } = useGeolocation();
  const canEdit = !!tripId;
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const items = useMemo(() => {
    const raw = T.restaurants?.[city] || [];
    let list = raw.filter((r) => priceMatchesFilter(r.price, priceFilter));
    if (noPorkOnly) list = list.filter((r) => r.noPork === true);

    // Distancia
    const withDist = list.map((r) => ({
      ...r,
      _distance: position && r.lat && r.lng ? distanceMeters(position, { lat: r.lat, lng: r.lng }) : null,
    }));

    if (sortBy === "rating") {
      withDist.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sortBy === "distance") {
      withDist.sort((a, b) => {
        if (a._distance == null && b._distance == null) return 0;
        if (a._distance == null) return 1;
        if (b._distance == null) return -1;
        return a._distance - b._distance;
      });
    } else if (sortBy === "price") {
      withDist.sort((a, b) => (a.price || 0) - (b.price || 0));
    }
    return withDist;
  }, [city, priceFilter, noPorkOnly, sortBy, position, T]);

  const openNew = () => setEditing({ ...emptyRestaurant(city), _new: true });
  const openEdit = (r) => setEditing(JSON.parse(JSON.stringify({ ...r, _city: city })));

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const { _new, _city, _distance, ...clean } = editing;
    clean.price = Number(clean.price) || 2;
    clean.rating = Number(clean.rating) || 4.0;
    clean.lat = Number(clean.lat) || 0;
    clean.lng = Number(clean.lng) || 0;
    const next = JSON.parse(JSON.stringify(T));
    next.restaurants = next.restaurants || {};
    next.restaurants[city] = next.restaurants[city] || [];
    if (_new) next.restaurants[city].push(clean);
    else next.restaurants[city] = next.restaurants[city].map((r) => (r.id === clean.id ? clean : r));
    try { await saveTripData(next); } finally { setSaving(false); setEditing(null); }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm("¿Eliminar este restaurante?")) return;
    setSaving(true);
    const next = JSON.parse(JSON.stringify(T));
    next.restaurants = next.restaurants || {};
    next.restaurants[city] = (next.restaurants[city] || []).filter((r) => r.id !== editing.id);
    try { await saveTripData(next); } finally { setSaving(false); setEditing(null); }
  };

  const e = editing;
  const setE = (patch) => setEditing((x) => ({ ...x, ...patch }));

  const openInMaps = (r) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}&travelmode=walking`;
    window.open(url, "_blank");
  };

  return (
    <div className="container-app pt-6 pb-28" data-testid="restaurants-page">
      {!hideBackLink && (
        <Link
          to={base || "/"}
          className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4"
          data-testid="restaurants-back"
        >
          <ChevronLeft size={16} /> Inicio
        </Link>
      )}
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-3xl font-bold leading-tight">Restaurantes</h1>
        {canEdit && (
          <button onClick={openNew} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[hsl(var(--accent))] text-white text-xs font-semibold active:scale-95" data-testid="add-restaurant-btn">
            <Plus size={14} /> Añadir
          </button>
        )}
      </div>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">
        Tradicionales y de referencia · {items.length} opciones
      </p>

      {/* City switcher */}
      <div className="flex gap-2 mb-4 overflow-x-auto hide-scrollbar">
        {CITIES.map((c) => (
          <button
            key={c}
            onClick={() => setCity(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              city === c
                ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
                : "bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))] border border-[hsl(var(--line))]"
            }`}
            data-testid={`restaurants-city-${c}`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Precio:</span>
        <button
          onClick={() => setPriceFilter("all")}
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            priceFilter === "all"
              ? "bg-[hsl(var(--accent))] text-white"
              : "bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))]"
          }`}
          data-testid="restaurants-price-all"
        >
          Todos
        </button>
        {[1, 2, 3, 4].map((p) => (
          <button
            key={p}
            onClick={() => setPriceFilter(p)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              priceFilter === p
                ? "bg-[hsl(var(--accent))] text-white"
                : "bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))]"
            }`}
            data-testid={`restaurants-price-${p}`}
          >
            {PRICE_LABELS[p - 1]}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          onClick={() => setNoPorkOnly((v) => !v)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
            noPorkOnly
              ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]"
              : "bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))]"
          }`}
          data-testid="restaurants-nopork"
        >
          Sin cerdo ✓
        </button>
        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold ml-auto">Ordenar:</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-2 py-1 rounded-full text-xs bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))]"
          data-testid="restaurants-sort"
        >
          <option value="rating">⭐ Rating</option>
          <option value="distance">📍 Cercanía</option>
          <option value="price">💰 Precio</option>
        </select>
      </div>

      {/* Lista */}
      <ul className="space-y-3">
        {items.map((r, i) => (
          <motion.li
            key={r.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.03 * i }}
            className="card-paper p-4"
            data-testid={`restaurant-${r.id}`}
          >
            <div className="flex items-start justify-between gap-3 mb-1">
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold leading-tight">{r.name}</h3>
                <p className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted))] mt-0.5">
                  {r.cuisine}
                </p>
              </div>
              <div className="shrink-0 flex flex-col items-end gap-0.5">
                {canEdit && (
                  <button onClick={() => openEdit(r)} className="w-7 h-7 rounded-full bg-[hsl(var(--bg))] border border-[hsl(var(--line))] flex items-center justify-center active:scale-95" data-testid={`edit-restaurant-${r.id}`}>
                    <Edit3 size={11} />
                  </button>
                )}
                <FavoriteButton kind="restaurant" ref={r.id} size={14} />
                <div className="flex items-center gap-1 text-xs font-semibold">
                  <Star size={11} className="fill-[hsl(var(--accent))] text-[hsl(var(--accent))]" />
                  <span>{r.rating?.toFixed(1)}</span>
                </div>
                <span className="text-xs font-bold text-[hsl(var(--ink-soft))]">
                  {PRICE_LABELS[r.price - 1]}
                </span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 mt-2 mb-2">
              {r.area && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--bg))] border border-[hsl(var(--line))] text-[hsl(var(--muted))]">
                  📍 {r.area}
                </span>
              )}
              {r.noPork && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--bg))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))]">
                  Sin cerdo ✓
                </span>
              )}
              {r.jewish && (
                <span className="px-2 py-0.5 rounded-full text-[10px] bg-[hsl(var(--bg))] border border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]">
                  ✡ Judío
                </span>
              )}
            </div>

            {r.signature && (
              <p className="text-xs serif text-[hsl(var(--ink-soft))] leading-[1.5] mb-2">
                <span className="text-[hsl(var(--accent))] font-semibold">›</span> {r.signature}
              </p>
            )}

            <div className="flex items-center gap-3 text-[11px] text-[hsl(var(--muted))] mb-3 flex-wrap">
              {r.hours && (
                <span className="inline-flex items-center gap-1">
                  <Clock size={11} /> {r.hours}
                </span>
              )}
              <span>{PRICE_TEXTS[r.price]}</span>
              {r._distance != null && (
                <span className="text-[hsl(var(--accent))] font-medium">
                  {formatDistance(r._distance)} · {walkingMinutes(r._distance)} min a pie
                </span>
              )}
            </div>

            {r.tip && (
              <p className="text-[11px] text-[hsl(var(--accent))] mb-3">💡 {r.tip}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => openInMaps(r)}
                className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-[hsl(var(--ink))] text-[hsl(var(--bg))] text-xs font-semibold active:scale-95"
                data-testid={`restaurant-go-${r.id}`}
              >
                <Navigation size={12} /> Llévame
              </button>
              {r.reserve && (
                <a
                  href={r.reserve.startsWith("+") ? `tel:${r.reserve.replace(/\s/g, "")}` : r.reserve.startsWith("http") ? r.reserve : `https://${r.reserve.split(" · ").pop()}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl bg-[hsl(var(--bg))] border border-[hsl(var(--line))] text-xs font-semibold text-[hsl(var(--ink-soft))] active:scale-95"
                  data-testid={`restaurant-reserve-${r.id}`}
                >
                  <Phone size={12} /> Reservar
                </a>
              )}
            </div>
          </motion.li>
        ))}
        {items.length === 0 && (
          <li className="text-center text-sm text-[hsl(var(--muted))] py-8">
            No hay restaurantes que coincidan con los filtros.
          </li>
        )}
      </ul>

      {/* EDIT RESTAURANT SHEET */}
      <EditSheet
        open={!!e}
        title={e?._new ? `Añadir restaurante en ${city}` : "Editar restaurante"}
        onClose={() => setEditing(null)}
        onSave={handleSave}
        onDelete={e?._new ? null : handleDelete}
        saving={saving}
      >
        {e && (
          <>
            <Field label="Nombre"><input value={e.name} onChange={(ev) => setE({ name: ev.target.value })} placeholder="Restaurante X" className={inputCls} data-testid="er-name" /></Field>
            <Field label="Tipo de cocina"><input value={e.cuisine} onChange={(ev) => setE({ cuisine: ev.target.value })} placeholder="Húngara · judía" className={inputCls} /></Field>
            <Field label="Barrio"><input value={e.area} onChange={(ev) => setE({ area: ev.target.value })} placeholder="Distrito VII" className={inputCls} /></Field>
            <Field label="Dirección"><input value={e.address} onChange={(ev) => setE({ address: ev.target.value })} placeholder="Dob u. 15" className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Latitud"><input type="number" step="0.000001" value={e.lat} onChange={(ev) => setE({ lat: ev.target.value })} placeholder="47.4960" className={inputCls} /></Field>
              <Field label="Longitud"><input type="number" step="0.000001" value={e.lng} onChange={(ev) => setE({ lng: ev.target.value })} placeholder="19.0598" className={inputCls} /></Field>
              <Field label="Precio (1=$ a 4=$$$$)">
                <select value={e.price} onChange={(ev) => setE({ price: ev.target.value })} className={inputCls}>
                  <option value={1}>$ (&lt; €15)</option>
                  <option value={2}>$$ (€15–35)</option>
                  <option value={3}>$$$ (€35–65)</option>
                  <option value={4}>$$$$ (&gt; €65)</option>
                </select>
              </Field>
              <Field label="Rating Google (0–5)"><input type="number" step="0.1" min="0" max="5" value={e.rating} onChange={(ev) => setE({ rating: ev.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Horarios"><input value={e.hours} onChange={(ev) => setE({ hours: ev.target.value })} placeholder="Lun–Sáb 12–23" className={inputCls} /></Field>
            <Field label="Plato signature"><input value={e.signature} onChange={(ev) => setE({ signature: ev.target.value })} placeholder="Goulash, paprikash" className={inputCls} /></Field>
            <Field label="Teléfono o web para reservar"><input value={e.reserve} onChange={(ev) => setE({ reserve: ev.target.value })} placeholder="+36 1 413 7488" className={inputCls} /></Field>
            <Field label="Tip"><textarea value={e.tip} onChange={(ev) => setE({ tip: ev.target.value })} placeholder="Reservar para la noche…" className={inputCls} rows={2} /></Field>
            <div className="flex items-center gap-4 mb-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!e.noPork} onChange={(ev) => setE({ noPork: ev.target.checked })} /> Sin cerdo
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!e.jewish} onChange={(ev) => setE({ jewish: ev.target.checked })} /> Judío
              </label>
            </div>
          </>
        )}
      </EditSheet>
    </div>
  );
}
